import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Flex, Layout, Popconfirm, Space, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import {
  appSettingsRepository,
  defaultFieldAppSettings,
  type FieldAppSettings,
  normalizeFieldAppSettings,
} from '@/app/settings/app-settings';
import { FIELD_AUTO_SYNC_STATUSES, type FieldAutoSyncStatus } from '@/features/sync/engine/field-auto-sync';
import { SettingsApiCard } from '@/routers/settings/components/SettingsApiCard';
import { fieldStoreNames } from '@/shared/db/schema';
import type { InventoryLocale } from '@/shared/i18n/inventory-i18n';
import { LanguageSwitcher } from '@/shared/i18n/LanguageSwitcher';
import { messages } from './messages';

interface SettingsState {
  clearedAt: string | null;
  error: string | null;
  isClearingLocalData: boolean;
  isLoading: boolean;
  isSaving: boolean;
  savedAt: string | null;
  settings: FieldAppSettings;
}

interface SettingsViewProps {
  autoSyncStatus?: FieldAutoSyncStatus;
  locale: InventoryLocale;
  onBack?: () => void;
  onLocaleChange: (locale: InventoryLocale) => void;
}

export function SettingsView({
  autoSyncStatus = FIELD_AUTO_SYNC_STATUSES.idle,
  locale,
  onBack,
  onLocaleChange,
}: SettingsViewProps) {
  const intl = useIntl();
  const { database } = useInventoryAppServices();
  const [state, setState] = useState<SettingsState>({
    clearedAt: null,
    error: null,
    isClearingLocalData: false,
    isLoading: true,
    isSaving: false,
    savedAt: null,
    settings: defaultFieldAppSettings,
  });

  useEffect(() => {
    async function load() {
      try {
        const settings = await appSettingsRepository.load();
        setState({
          clearedAt: null,
          error: null,
          isClearingLocalData: false,
          isLoading: false,
          isSaving: false,
          savedAt: null,
          settings,
        });
      } catch (error) {
        setState(current => ({
          ...current,
          error: error instanceof Error ? error.message : intl.formatMessage(messages.settingsErrorLoadSettings),
          isLoading: false,
        }));
      }
    }

    void load();
  }, [intl.formatMessage]);

  const normalizedSettings = useMemo(() => normalizeFieldAppSettings(state.settings), [state.settings]);
  const viteDefaultApiBaseUrl = defaultFieldAppSettings.apiBaseUrl;

  function updateSettings(patch: Partial<FieldAppSettings>) {
    setState(current => ({
      ...current,
      clearedAt: null,
      error: null,
      savedAt: null,
      settings: {
        ...current.settings,
        ...patch,
      },
    }));
  }

  async function saveSettings(input: FieldAppSettings = state.settings) {
    const settings = normalizeFieldAppSettings(input);
    setState(current => ({ ...current, clearedAt: null, error: null, isSaving: true, settings }));

    try {
      const saved = await appSettingsRepository.save(settings);
      setState({
        clearedAt: null,
        error: null,
        isClearingLocalData: false,
        isLoading: false,
        isSaving: false,
        savedAt: new Date().toISOString(),
        settings: saved,
      });
    } catch (error) {
      setState(current => ({
        ...current,
        error: error instanceof Error ? error.message : intl.formatMessage(messages.settingsErrorSaveSettings),
        isSaving: false,
      }));
    }
  }

  function resetApiBaseUrl() {
    const settings = {
      ...state.settings,
      apiBaseUrl: viteDefaultApiBaseUrl,
    };
    void saveSettings(settings);
  }

  async function clearLocalData() {
    setState(current => ({ ...current, clearedAt: null, error: null, isClearingLocalData: true }));

    try {
      await Promise.all(fieldStoreNames.map(storeName => database.clear(storeName)));
      setState(current => ({
        ...current,
        clearedAt: new Date().toISOString(),
        error: null,
        isClearingLocalData: false,
      }));
    } catch (error) {
      setState(current => ({
        ...current,
        error: error instanceof Error ? error.message : intl.formatMessage(messages.settingsErrorClearLocalData),
        isClearingLocalData: false,
      }));
    }
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Content style={{ padding: 16 }}>
        <Flex align="flex-start" justify="center">
          <Card style={{ maxWidth: 560, width: '100%' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Flex align="flex-start" gap="middle" justify="space-between" wrap>
                <Space direction="vertical" size={4}>
                  <Typography.Text type="secondary">
                    {intl.formatMessage(messages.settingsFieldInventory)}
                  </Typography.Text>
                  <Typography.Title level={2} style={{ margin: 0 }}>
                    {intl.formatMessage(messages.settingsTitle)}
                  </Typography.Title>
                  <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                    {intl.formatMessage(messages.settingsDescription)}
                  </Typography.Paragraph>
                </Space>
                {onBack ? (
                  <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
                    {intl.formatMessage(messages.appCommonBack)}
                  </Button>
                ) : null}
              </Flex>

              {state.error ? <Alert closable message={state.error} showIcon type="error" /> : null}
              {state.savedAt ? (
                <Alert
                  closable
                  message={intl.formatMessage(messages.settingsSavedAt, {
                    time: new Date(state.savedAt).toLocaleTimeString(),
                  })}
                  showIcon
                  type="success"
                />
              ) : null}
              {state.clearedAt ? (
                <Alert
                  closable
                  message={intl.formatMessage(messages.settingsLocalDataCleared)}
                  showIcon
                  type="success"
                />
              ) : null}

              <Card size="small" title={intl.formatMessage(messages.languageTitle)}>
                <LanguageSwitcher onChange={onLocaleChange} value={locale} />
              </Card>

              <SettingsApiCard
                autoSyncStatus={autoSyncStatus}
                defaultApiBaseUrl={viteDefaultApiBaseUrl}
                isLoading={state.isLoading}
                isSaving={state.isSaving}
                normalizedSettings={normalizedSettings}
                onResetApiBaseUrl={resetApiBaseUrl}
                onSave={() => void saveSettings()}
                onUpdate={updateSettings}
                settings={state.settings}
              />

              <Card size="small" title={intl.formatMessage(messages.settingsLocalDataTitle)}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                    {intl.formatMessage(messages.settingsLocalDataDescription)}
                  </Typography.Paragraph>
                  <Popconfirm
                    okButtonProps={{ danger: true }}
                    okText={intl.formatMessage(messages.settingsClearLocalDataConfirm)}
                    onConfirm={() => void clearLocalData()}
                    title={intl.formatMessage(messages.settingsClearLocalDataPrompt)}
                  >
                    <Button danger loading={state.isClearingLocalData}>
                      {intl.formatMessage(messages.settingsClearLocalData)}
                    </Button>
                  </Popconfirm>
                </Space>
              </Card>
            </Space>
          </Card>
        </Flex>
      </Layout.Content>
    </Layout>
  );
}
